import {
  TravellerWithCollectionMembershipsDTO,
  useInactivateCollectionMember,
  useActivateCollectionMember,
  getGetTravellerByIDQueryKey,
  getGetTravellersForDepartmentQueryKey,
} from '@/api/trafikportalen';
import {
  formatScheduledInactivation,
  getMembershipStatus,
} from '@/helpers/membershipStatus';
import { useCollectionMemberDelete } from '@/hooks/useCollectionMemberDelete';
import { useTenantID } from '@/hooks/useTenantID';
import { IconArrowRight, IconPlus, IconTrash } from '@/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { ButtonProps } from '../core/Button';
import IconButton from '../core/IconButton';
import Input from '../core/Input';
import { FormSection } from '../FormSection';
import { WarningDialog } from '../WarningDialog';
import { toast } from '@/hooks/useToast';
import Button from '../core/Button';
import { DateTime } from 'luxon';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { AddTravellerToDepartmentForm } from './AddTravellerToDepartmentForm';
import { DialogWindow } from '../Dialog';

interface MembershipItemProps {
  membership: TravellerWithCollectionMembershipsDTO['memberships'][0];
  tenantId: string;
  onInactivate: (membershipId: string) => void;
  onDelete: (membershipId: string) => void;
}

const MembershipItem = ({
  membership,
  tenantId,
  onInactivate,
  onDelete,
}: MembershipItemProps) => {
  const { formatMessage } = useIntl();

  const status = getMembershipStatus(membership);

  let buttonColor: ButtonProps['color'] = 'base';
  let buttonVariant: ButtonProps['variant'] = 'ghost';
  let buttonText = formatMessage({ defaultMessage: 'Inaktivera' });
  if (status === 'inactive') {
    buttonColor = 'red';
    buttonVariant = 'filled';
    buttonText = hasDepartmentWriteAccess(membership.department_id)
      ? formatMessage({
          defaultMessage: 'Återaktivera',
        })
      : formatMessage({
          defaultMessage: 'Inaktiv',
        });
  } else if (
    status === 'will-inactivate' ||
    status === 'pending-inactivation'
  ) {
    buttonColor = 'yellow';
    buttonVariant = 'filled';
    buttonText = formatScheduledInactivation(
      formatMessage,
      membership.inactivate_at,
    );
  }

  return (
    <div className="flex w-full items-center gap-1">
      <Button
        to="/$tenantId/departments/$departmentId/$view"
        params={{
          tenantId,
          view: 'travellers',
          departmentId: membership.department_id,
        }}
        className="w-full justify-between overflow-hidden"
        endIcon={<IconArrowRight aria-hidden="true" />}
      >
        <p className="min-w-0 flex-1 truncate">{membership.department_name}</p>
      </Button>
      {hasDepartmentWriteAccess(membership.department_id) ? (
        <>
          <Button
            title={buttonText}
            variant={buttonVariant}
            onClick={() => onInactivate(membership.collection_member_id)}
            color={buttonColor}
            className="min-w-max"
          >
            {buttonText}
          </Button>
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Ta bort' })}
            title={formatMessage({ defaultMessage: 'Ta bort' })}
            icon={<IconTrash />}
            variant="ghost"
            color="red"
            onClick={() => onDelete(membership.collection_member_id)}
          />
        </>
      ) : (
        <p className="ml-2">{buttonText}</p>
      )}
    </div>
  );
};

export const TravellerMemberships = ({
  traveller,
}: {
  traveller: TravellerWithCollectionMembershipsDTO;
}) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const inactivateCollectionMember = useInactivateCollectionMember();
  const activateCollectionMember = useActivateCollectionMember();
  const { deleteCollectionMember, isLoading: isDeleting } =
    useCollectionMemberDelete();

  const [inactivateCollectionMemberDate, setInactivateCollectionMemberDate] =
    useState<string>('');
  const [inactivateCollectionMemberId, setInactivateCollectionMemberId] =
    useState<string>('');
  const [reactivateCollectionMemberId, setReactivateCollectionMemberId] =
    useState<string>('');
  const [deleteCollectionMemberId, setDeleteCollectionMemberId] =
    useState<string>('');
  const [addToDepartmentOpen, setAddToDepartmentOpen] =
    useState<boolean>(false);

  const onInactivateMembership = useCallback(() => {
    if (!inactivateCollectionMemberId) {
      return;
    }

    const departmentId = traveller.memberships.find(
      (membership) =>
        membership.collection_member_id === inactivateCollectionMemberId,
    )?.department_id;
    if (!departmentId) {
      return;
    }

    const inactivateAt = inactivateCollectionMemberDate
      ? DateTime.fromFormat(inactivateCollectionMemberDate, 'yyyy-MM-dd')
          .endOf('day')
          .toISO()
      : undefined;

    inactivateCollectionMember.mutate(
      {
        collectionMemberID: inactivateCollectionMemberId,
        data: {
          inactivate_at: inactivateAt ?? undefined,
        },
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetTravellerByIDQueryKey(traveller.traveller.id),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetTravellersForDepartmentQueryKey(departmentId),
            }),
          ]);
          toast.success(
            {
              title: formatMessage({
                defaultMessage: `Ändringar sparade`,
              }),
            },
            { autoClose: 3000 },
          );
          setInactivateCollectionMemberId('');
        },
      },
    );
  }, [
    formatMessage,
    inactivateCollectionMember,
    inactivateCollectionMemberDate,
    inactivateCollectionMemberId,
    queryClient,
    traveller.traveller.id,
    traveller.memberships,
  ]);

  const onReactivateMembership = useCallback(() => {
    if (!reactivateCollectionMemberId) {
      return;
    }

    activateCollectionMember.mutate(
      {
        collectionMemberID: reactivateCollectionMemberId,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetTravellerByIDQueryKey(traveller.traveller.id),
            }),
          ]);
          toast.success(
            {
              title: formatMessage({
                defaultMessage: `Resenären har återaktiverats för avdelningen`,
              }),
            },
            { autoClose: 3000 },
          );
          setReactivateCollectionMemberId('');
        },
      },
    );
  }, [
    activateCollectionMember,
    formatMessage,
    queryClient,
    reactivateCollectionMemberId,
    traveller.traveller.id,
  ]);

  const masterMembershipCount = traveller.memberships.filter(
    (m) => m.collection_type === 'master',
  ).length;

  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Avdelningar" />}
      description={
        <FormattedMessage
          defaultMessage="Resenären är medlem i följande {count, plural, one {avdelning} other {avdelningar}}:"
          values={{ count: masterMembershipCount }}
        />
      }
    >
      {traveller.memberships
        .filter((m) => m.collection_type === 'master')
        .map((membership) => {
          const handleInactivate = (membershipId: string) => {
            setInactivateCollectionMemberDate('');
            if (membership.inactivate_at || !membership.active) {
              setReactivateCollectionMemberId(membershipId);
            } else {
              setInactivateCollectionMemberId(membershipId);
            }
          };

          return (
            <MembershipItem
              key={membership.collection_id}
              membership={membership}
              tenantId={tenantId}
              onInactivate={handleInactivate}
              onDelete={setDeleteCollectionMemberId}
            />
          );
        })}
      <Button
        variant="outline"
        startIcon={<IconPlus />}
        onClick={() => setAddToDepartmentOpen(true)}
        className="w-full"
      >
        <FormattedMessage defaultMessage="Lägg till i annan avdelning" />
      </Button>
      <WarningDialog
        title={formatMessage({ defaultMessage: 'Inaktivera' })}
        content={
          <div className="flex flex-col gap-10">
            <FormattedMessage defaultMessage="Är du säker på att du vill inaktivera resenären för denna avdelningen?" />
            <br />
            <FormattedMessage defaultMessage="Biljetter köpta i portalen, tjänsteresa i app och tjänstresa på kort kommer avslutas (kan upp till 5 minuter). Biljetter köpta på kort eller i app avslutas inte." />
            <br />
            <FormattedMessage defaultMessage="Det angivna datumet blir resenärens sista aktiva dag i avdelningen." />
            <Input
              id="inactivate-collection-member-date"
              label={formatMessage({ defaultMessage: 'Inaktiveringsdatum' })}
              type="date"
              value={inactivateCollectionMemberDate}
              onChange={(e) =>
                setInactivateCollectionMemberDate(e.target.value)
              }
              min={DateTime.now().toFormat('yyyy-MM-dd')}
              helperText={formatMessage({
                defaultMessage:
                  'Om du inte anger ett datum kommer resenären att inaktiveras direkt.',
              })}
              helperTextReserveSpace
            />
          </div>
        }
        onCancel={() => setInactivateCollectionMemberId('')}
        buttonText={formatMessage({ defaultMessage: 'Inaktivera' })}
        loading={inactivateCollectionMember.isPending}
        onConfirm={() => {
          onInactivateMembership();
        }}
        open={!!inactivateCollectionMemberId}
      />
      <WarningDialog
        title={formatMessage({ defaultMessage: 'Återaktivera' })}
        content={
          <FormattedMessage defaultMessage="Är du säker på att du vill återaktivera resenären för denna avdelningen?" />
        }
        onCancel={() => setReactivateCollectionMemberId('')}
        buttonText={formatMessage({ defaultMessage: 'Återaktivera' })}
        loading={activateCollectionMember.isPending}
        onConfirm={() => {
          onReactivateMembership();
        }}
        open={!!reactivateCollectionMemberId}
      />
      <WarningDialog
        title={formatMessage({ defaultMessage: 'Ta bort från avdelning' })}
        content={
          <>
            {traveller.memberships.length === 1 ? (
              <FormattedMessage defaultMessage="Resenären är enbart medlem i denna avdelning och kommer därmed raderas." />
            ) : (
              <FormattedMessage defaultMessage="Är du säker på att du vill ta bort resenären från avdelningen?" />
            )}
          </>
        }
        onCancel={() => setDeleteCollectionMemberId('')}
        buttonText={formatMessage({ defaultMessage: 'Ta bort' })}
        buttonIcon={<IconTrash />}
        loading={isDeleting}
        onConfirm={() => {
          deleteCollectionMember(traveller, deleteCollectionMemberId);
          setDeleteCollectionMemberId('');
        }}
        open={!!deleteCollectionMemberId}
      />
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till i annan avdelning',
        })}
        content={
          <AddTravellerToDepartmentForm
            traveller={traveller}
            onCancel={() => setAddToDepartmentOpen(false)}
            onSuccess={() => setAddToDepartmentOpen(false)}
          />
        }
        onOpenChange={setAddToDepartmentOpen}
        open={addToDepartmentOpen}
      />
    </FormSection>
  );
};
